/**
 * Cognito Authentication Module for Shopify
 * 通过 AWS Lambda 处理登录和密码恢复流程
 */

(function() {
  'use strict';

  const PASSWORD_REQUIREMENTS_MESSAGE = 'The password must be at least 8 characters long and must include uppercase and lowercase letters, numbers, and at least one supported special symbol.';
  const PASSWORD_SPECIAL_SYMBOL_PATTERN = /[\^$*.[\]{}()?"!@#%&\/\\,><':;|_~`=+-]/;

  // 主应用命名空间
  const CognitoAuth = {
    // 存储 DOM 元素引用
    elements: {},
    
    // 存储当前上下文数据
    state: {
      currentRecoverEmail: '',
      isProcessing: false
    },
    
    // 初始化模块
    init: function() {
      // 缓存 DOM 元素
      this.cacheElements();
      
      // 设置事件监听器
      this.bindEvents();
      
      // 检查 URL 参数并处理页面状态
      this.handleInitialPageState();
    },
    
    // 缓存常用 DOM 元素
    cacheElements: function() {
      // 恢复密码元素
      this.elements.recoverStep1 = document.getElementById('recover-step-1');
      this.elements.recoverStep2 = document.getElementById('recover-step-2');
      this.elements.recoverEmail = document.getElementById('RecoverEmail');
      this.elements.recoverButton = document.getElementById('cognito-recover-button');
      this.elements.recoverResult1 = document.getElementById('recover-result-1');
      
      // 重置密码元素
      this.elements.verificationCode = document.getElementById('verification-code');
      this.elements.newPassword = document.getElementById('new-password');
      this.elements.confirmPassword = document.getElementById('confirm-password');
      this.elements.resetPasswordButton = document.getElementById('reset-password-button');
      this.elements.resendCodeButton = document.getElementById('resend-code-button');
      this.elements.recoverResult2 = document.getElementById('recover-result-2');
      
      // 登录元素
      this.elements.customerEmail = document.getElementById('CustomerEmail');
      this.elements.customerPassword = document.getElementById('CustomerPassword');
      this.elements.signInButton = document.getElementById('cognito-sign-in');
      this.elements.loginResult = document.getElementById('login-result');
      this.elements.resetSuccessMessage = document.getElementById('reset-success-message');
      this.elements.forgotPasswordLink = document.getElementById('forgot-password-link');
    },
    

    handleTrackEvent: function(eventData) {
      if (window.apiLogger) {
        window.apiLogger.logApiCall({
          eventName: eventData.eventName,
          userName: eventData.userName,
          status: eventData.status !== undefined ? eventData.status.toString() : undefined,
          requestBody: eventData.requestBody,
          responseBody: eventData.responseBody
        });
      }
    },
 

    // 设置事件监听器
    bindEvents: function() {
      // 密码恢复
      if (this.elements.recoverButton) {
        this.elements.recoverButton.addEventListener('click', this.handleRequestResetCode.bind(this));
      }
      
      // 重置密码
      if (this.elements.resetPasswordButton) {
        this.elements.resetPasswordButton.addEventListener('click', this.handleConfirmResetPassword.bind(this));
      }
      
      // 重新发送验证码
      if (this.elements.resendCodeButton) {
        this.elements.resendCodeButton.addEventListener('click', this.handleResendVerificationCode.bind(this));
      }
      
      // 登录
      if (this.elements.signInButton) {
        this.elements.signInButton.addEventListener('click', this.handleLogin.bind(this));
      }
      
      // 从登录表单复制邮箱到密码恢复表单
      if (this.elements.forgotPasswordLink) {
        this.elements.forgotPasswordLink.addEventListener('click', this.handleForgotPasswordClick.bind(this));
      }
    },
    
    /**
     * 获取市场前缀（支持多市场 URL 格式）
     * @returns {String} 市场前缀（如 '/de-de', '/de', '/en-us'），无市场时返回空字符串
     *
     * 支持的格式：
     * - /de-de/account → '/de-de' (语言-国家代码)
     * - /de/account    → '/de' (仅语言代码)
     * - /account       → '' (默认市场)
     */
    getMarketPrefix: function() {
      const pathname = window.location.pathname;

      // 匹配格式：/xx/ 或 /xx-yy/ (语言代码或语言-国家代码)
      const marketMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//);

      return marketMatch ? '/' + marketMatch[1] : '';
    },

    // 处理初始页面状态，基于 URL 和 localStorage
    handleInitialPageState: function() {
      // 从登录表单自动复制邮箱（如果有）
      setTimeout(() => {
        if (this.elements.recoverEmail && this.elements.customerEmail) {
          const loginEmail = this.elements.customerEmail.value.trim();
          if (loginEmail) {
            this.elements.recoverEmail.value = loginEmail;
          }
        }
      }, 100);
      
      // 检查是否已通过 Cognito 认证但未登录到 Shopify
      if (localStorage.getItem('cognito_authenticated') === 'true' &&
          localStorage.getItem('idToken') &&
          this.elements.customerEmail) {

        // 清除认证标志以防止循环
        localStorage.removeItem('cognito_authenticated');

        // 获取市场前缀并重定向到对应市场的 account 页面
        const marketPrefix = this.getMarketPrefix();
        window.location.href = marketPrefix + '/account';
      }
    },
    
    // 处理忘记密码链接点击
    handleForgotPasswordClick: function() {
      const email = this.elements.customerEmail.value.trim();
      if (email) {
        setTimeout(() => {
          this.elements.recoverEmail.value = email;
        }, 100);
      }
    },
    
    // 处理请求密码重置码
    handleRequestResetCode: async function(event) {
      if (event) event.preventDefault();
      
      if (this.state.isProcessing) return;
      
      const email = this.elements.recoverEmail.value.trim();
      
      // 验证输入
      if (!email) {
        this.UI.showResult('Please enter your email address', 'error', this.elements.recoverResult1);
        return;
      }
      
      if (!this.Validation.isValidEmail(email)) {
        this.UI.showResult('Please enter a valid email address', 'error', this.elements.recoverResult1);
        return;
      }
      
      this.state.isProcessing = true;
      this.UI.showResult('Sending verification code...', 'processing', this.elements.recoverResult1);
      
      try {
        // 保存当前邮箱以供后续使用
        this.state.currentRecoverEmail = email;
        
        // 发送 API 请求
        const response = await fetch(`${CognitoApiConfig.baseUrl}/request-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: email })
        });
        
        const data = await response.json();

        this.handleTrackEvent({userName: email, status: data.success, eventName:`/request-reset`,requestBody: { email: email },responseBody: data})

        
        this.state.isProcessing = false;
        
        if (!response.ok || !data.success) {
          console.error('Password reset request failed: ', data);
          this.UI.showResult(data.message || 'Password reset request failed', 'error', this.elements.recoverResult1);
          return;
        }
        
        console.log('Password reset code sent successfully:', data);
        
        // 切换到步骤 2：输入验证码和新密码
        this.elements.recoverStep1.style.display = 'none';
        this.elements.recoverStep2.style.display = 'block';
        
      } catch (error) {
        this.state.isProcessing = false;
        this.handleTrackEvent({
          userName: email,
          status: 'code_error',
          eventName: `/request-reset`,
          requestBody: { email: email },
          responseBody: { error: error.message }
        });
        console.error('Error during password reset request:', error);
        this.UI.showResult('Error during password reset request: ' + error.message, 'error', this.elements.recoverResult1);
      }
    },
    
    // 处理确认密码重置
    handleConfirmResetPassword: async function(event) {
      if (event) event.preventDefault();
      
      if (this.state.isProcessing) return;
      
      const code = this.elements.verificationCode.value.trim();
      const newPassword = this.elements.newPassword.value;
      const confirmPassword = this.elements.confirmPassword.value;
      
      // 验证输入
      if (!code) {
        this.UI.showResult('Please enter the verification code', 'error', this.elements.recoverResult2);
        return;
      }
      
      if (!newPassword) {
        this.UI.showResult('Please enter a new password', 'error', this.elements.recoverResult2);
        return;
      }
      
      if (newPassword !== confirmPassword) {
        this.UI.showResult('Passwords do not match', 'error', this.elements.recoverResult2);
        return;
      }
      
      if (!this.Validation.isValidPassword(newPassword)) {
        this.UI.showResult(PASSWORD_REQUIREMENTS_MESSAGE, 'error', this.elements.recoverResult2);
        return;
      }
      
      this.state.isProcessing = true;
      this.UI.showResult('Resetting password...', 'processing', this.elements.recoverResult2);
      
      try {

        const requestBodyData = {
          email: this.state.currentRecoverEmail,
          code: code,
          newPassword: newPassword
        }

        // 发送 API 请求
        const response = await fetch(`${CognitoApiConfig.baseUrl}/confirm-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBodyData)
        });

        const data = await response.json();

        await this.handleTrackEvent({ userName: this.state.currentRecoverEmail, status: data.success,eventName:`/confirm-reset`,requestBody: requestBodyData,responseBody: data})


        this.state.isProcessing = false;
        
        if (!response.ok || !data.success) {
          console.error('Password reset failed: ', data);
          this.UI.showResult(data.message || 'Password reset failed', 'error', this.elements.recoverResult2);
          return;
        }
        
        console.log('Password reset successful:', data);
        
        // 显示成功消息
        this.UI.showResult('Your password has been reset successfully', 'success', this.elements.recoverResult2);
        
        // 延迟后重定向到登录页面
        setTimeout(() => {
          // 将URL中添加reset_success参数
          const url = new URL(window.location.href);
          url.hash = 'login';
          url.searchParams.set('reset_success', 'true');
          window.location.href = url.toString();
          
          // 显示密码重置成功消息  
          if (this.elements.resetSuccessMessage) {
            this.elements.resetSuccessMessage.style.display = 'block';
          }
          
          // 用邮箱自动填充登录表单
          if (this.elements.customerEmail) {
            this.elements.customerEmail.value = this.state.currentRecoverEmail;
            this.elements.customerPassword.focus();
          }
        }, 2000);
        
      } catch (error) {
        this.state.isProcessing = false;
        console.error('Error during password reset confirmation:', error);
        this.UI.showResult('Error during password reset: ' + error.message, 'error', this.elements.recoverResult2);
      }
    },
    
    // 处理重新发送验证码
    handleResendVerificationCode: async function(event) {
      if (event) event.preventDefault();
      
      if (this.state.isProcessing) return;
      
      if (!this.state.currentRecoverEmail) {
        this.UI.showResult('Email address is missing. Please start the password reset process again.', 'error', this.elements.recoverResult2);
        return;
      }
      
      this.state.isProcessing = true;
      this.UI.showResult('Resending verification code...', 'processing', this.elements.recoverResult2);
      
      try {
        // 发送 API 请求
        const response = await fetch(`${CognitoApiConfig.baseUrl}/resend-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: this.state.currentRecoverEmail })
        });
        
        const data = await response.json();

        this.handleTrackEvent({ userName: this.state.currentRecoverEmail, status: data.success,eventName:`/resend-code`,requestBody: { email: this.state.currentRecoverEmail },responseBody: data})

        
        this.state.isProcessing = false;
        
        if (!response.ok || !data.success) {
          console.error('Failed to resend verification code:', data);
          this.UI.showResult(data.message || 'Failed to resend verification code', 'error', this.elements.recoverResult2);
          return;
        }
        
        console.log('Verification code resent successfully:', data);
        this.UI.showResult('Verification code has been resent to your email', 'success', this.elements.recoverResult2);
        
      } catch (error) {
        this.state.isProcessing = false;
        this.handleTrackEvent({
          status: 'code_error',
          eventName: `/resend-code`,
          requestBody: { email: this.state.currentRecoverEmail },
          responseBody: { error: error.message }
        });
        console.error('Error resending verification code:', error);
        this.UI.showResult('Error resending verification code: ' + error.message, 'error', this.elements.recoverResult2);
      }
    },
    
    // 处理 Cognito 登录
    handleLogin: async function(event) {
      if (event) event.preventDefault();
      
      if (this.state.isProcessing) return;
      
      const username = this.elements.customerEmail.value;
      const password = this.elements.customerPassword.value;

      // 获取市场前缀，构建登录后的目标路径（支持多市场）
      const marketPrefix = this.getMarketPrefix();
      const target_path = marketPrefix + '/account';
      const site_domain = window.location.host;
      console.log('target_path:', target_path);
      // 验证输入
      if (!username || !password) {
        this.UI.showResult('Please enter your email and password', 'error', this.elements.loginResult);
        return;
      }
      
      this.state.isProcessing = true;

      // 添加登录中状态提示
      this.UI.showResult('Logging in...', 'processing', this.elements.loginResult);

      try {
        const requestBodyData = { username: username, password: password, target_path: target_path,site_domain:site_domain }
        // 发送 API 请求
        const response = await fetch(`${CognitoApiConfig.baseUrl}/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBodyData)
        });

        const data = await response.json();

        this.state.isProcessing = false;
        await this.handleTrackEvent({userName: username, status: data.success, eventName:`/login`,requestBody: requestBodyData ,responseBody: data})
        if (!response.ok || !data.success) {
          console.error('Authentication failed:', data);
          this.UI.showResult(data.message || 'Authentication failed', 'error', this.elements.loginResult);
          return;
        }
        
        console.log('Authentication successful:', data);
        
        // 处理成功认证
        if (data.requiresNewPassword) {
          this.UI.showResult('You need to change your password. This is your first login or an administrator has reset your password.', 'error', this.elements.loginResult);
        } else {
          // 获取并存储令牌
          const tokens = data.tokens;
          if (tokens) {
            localStorage.setItem('idToken', tokens.idToken);
            localStorage.setItem('accessToken', tokens.accessToken);
            localStorage.setItem('refreshToken', tokens.refreshToken);
          }
          
          // 显示登录成功消息，然后重定向
          this.UI.showResult('Login successful! Redirecting...', 'success', this.elements.loginResult);
          if (window.IdentityService) {
            const cognitoSub = data.sub || data.userSub || window.IdentityService.getCognitoSub(tokens && tokens.idToken);
            window.IdentityService.bindAndSync({
              email: username,
              source: 'login',
              cognitoSub: cognitoSub
            });
          }
          document.dispatchEvent(new CustomEvent('gtm:account-authenticated', {
            detail: { eventName: 'login' }
          }));
          
          // 如果有重定向 URL，则使用它
          if (data.result) {
            // 延迟重定向，让用户看到成功消息
            setTimeout(() => {
              window.location.href = data.result;
            }, 1500);
          } else {
            // 如果没有重定向 URL，使用备用方法
            setTimeout(() => {
              this.fallbackFormSubmission(username, tokens);
            }, 1500);
          }
        }
        
      } catch (error) {
        this.state.isProcessing = false;
        this.handleTrackEvent({
          userName: username,
          status:'code_error',
          eventName: `/login`,
          requestBody: { username: username, password: password, target_path: target_path },
          responseBody: { error: error.message }
        });
        console.error('Error during login:', error);
        this.UI.showResult('Error during login: ' + error.message, 'error', this.elements.loginResult);
      }
    },
    
    // Shopify 登录的备用表单提交方法
    fallbackFormSubmission: function(email, tokens) {
      const password = this.elements.customerPassword.value;
      
      // 创建隐藏表单用于 Shopify 登录
      const shopifyForm = document.createElement('form');
      shopifyForm.method = 'POST';
      shopifyForm.action = '/account/login';
      shopifyForm.style.display = 'none';
      
      // 添加标准 Shopify 登录字段
      const emailField = document.createElement('input');
      emailField.type = 'hidden';
      emailField.name = 'customer[email]';
      emailField.value = email;
      
      const passwordInput = document.createElement('input');
      passwordInput.type = 'hidden';
      passwordInput.name = 'customer[password]';
      passwordInput.value = password;
      
      // 添加令牌信息到表单
      const tokenField = document.createElement('input');
      tokenField.type = 'hidden';
      tokenField.name = 'cognito_token';
      tokenField.value = tokens ? tokens.idToken : '';
      
      // 添加状态字段
      const formState = document.createElement('input');
      formState.type = 'hidden';
      formState.name = 'form_type';
      formState.value = 'customer_login';
      
      // 如果存在，添加返回 URL
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('return_url')) {
        const returnUrlField = document.createElement('input');
        returnUrlField.type = 'hidden';
        returnUrlField.name = 'return_url';
        returnUrlField.value = urlParams.get('return_url');
        shopifyForm.appendChild(returnUrlField);
      }
      
      // 将字段添加到表单
      shopifyForm.appendChild(emailField);
      shopifyForm.appendChild(passwordInput);
      shopifyForm.appendChild(tokenField);
      shopifyForm.appendChild(formState);
      
      // 将表单添加到页面并提交
      document.body.appendChild(shopifyForm);
      shopifyForm.submit();
    },
    
    // UI 相关函数
    UI: {
      // 显示结果消息
      showResult: function(message, type, resultElement, autoHide = false) {
        if (!resultElement) return;
        
        resultElement.innerHTML = message;
        resultElement.className = 'result';
        resultElement.classList.add(type);
        if (autoHide) {
          resultElement.classList.add('auto-hide');
        }
        resultElement.style.display = 'block';
        
        // 如果是自动隐藏的消息，设置定时器让它消失
        if (autoHide) {
          setTimeout(() => {
            resultElement.style.display = 'none';
          }, 3500);
        }
      },
      
      // 隐藏结果消息
      hideResult: function(resultElement) {
        if (resultElement) resultElement.style.display = 'none';
      },
      
      // 显示登录结果消息
      showLoginResult: function(message, type, autoHide = true) {
        const resultElement = document.getElementById('login-result');
        this.showResult(message, type, resultElement, autoHide);
      }
    },
    
    // 验证函数
    Validation: {
      // 验证邮箱格式
      isValidEmail: function(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
      },
      
      // 验证密码强度
      isValidPassword: function(password) {
        // 检查长度至少为 8 个字符
        if (password.length < 8) {
          return false;
        }
        
        // 检查是否有大写字母
        const hasUppercase = /[A-Z]/.test(password);
        
        // 检查是否有小写字母
        const hasLowercase = /[a-z]/.test(password);
        
        // 检查是否有数字
        const hasNumber = /[0-9]/.test(password);
        
        // 检查是否有 Cognito 支持的特殊字符
        const hasSpecialChar = PASSWORD_SPECIAL_SYMBOL_PATTERN.test(password);
        
        // 必须满足所有条件
        return hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
      }
    },
    
    // 安全相关函数
    Security: {
      // 这个函数现在留空，不再需要计算 SECRET_HASH
      calculateSecretHash: async function(username) {
        return null;
      }
    }
  };
  
  // 在 DOM 加载完成时初始化
  document.addEventListener('DOMContentLoaded', function() {
    CognitoAuth.init();
  });
})();
